import type { AgencyWage, NurseFullRole } from "../../../utils/models";
import { getShortRole } from "../../../utils/utilFunctions";

/*
 * DAT-906 · Team → Agency Configuration's grid, as data: its columns and rows, and how a cell reads. Shared
 * by the editable grid (`AgencyWagesTable`) and the wage breakdown's read-only preview of an agency's row.
 */

// Special role names for T&T — non-standard "roles" that share the wage table but represent
// terms / billing-hours rather than per-role rates, so they format and live separately from the
// per-role columns built from `departmentNurseTypes`.
const TT_TERMS_ROLE = "T&T Terms";
const TT_BILL_ROLE = "T&T Bill";

/** A rate column: the header it shows (a short role) and the role name the backend stores. */
export type AgencyDisplayColumn = { display: string; backend: string };

export type AgencyRow = {
	agencyName: string;
	// Keys are FULL role names. `wage: null` is a configured-but-blank rate, which is distinct from a
	// real 0 — the backend propagates 0 to nurses and leaves null alone, so the grid must not conflate
	// them on the way back out.
	roleWages: Record<string, { wage: number | null; wageId?: string }>;
};

/** The grid's rate columns in order: the two T&T columns, then one per role of the department. */
export const agencyDisplayColumns = (departmentNurseTypes: readonly NurseFullRole[]): AgencyDisplayColumn[] => [
	{ display: TT_TERMS_ROLE, backend: TT_TERMS_ROLE },
	{ display: TT_BILL_ROLE, backend: TT_BILL_ROLE },
	...departmentNurseTypes.map((fullRole) => ({ display: getShortRole(fullRole), backend: fullRole })),
];

/** One row per agency, its rates keyed by role. */
export const agencyRowsOf = (agencyWages: readonly AgencyWage[]): AgencyRow[] => {
	const rowsMap: Record<string, AgencyRow> = {};
	for (const wage of agencyWages) {
		if (!wage.agencyName) continue;
		if (!rowsMap[wage.agencyName]) {
			rowsMap[wage.agencyName] = { agencyName: wage.agencyName, roleWages: {} };
		}
		// Carried through as-is: a stored 0 is a real rate and a stored null is a blank cell. Only the
		// display and the editors coerce to 0.
		rowsMap[wage.agencyName].roleWages[wage.role] = { wage: wage.wage ?? null, wageId: wage.id };
	}
	return Object.values(rowsMap);
};

/** The T&T columns hold hours, not money. */
export const isAgencyHoursColumn = (backendRole: string): boolean => backendRole === TT_TERMS_ROLE || backendRole === TT_BILL_ROLE;

/** The T&T Bill column: how many hours before the shift a request to replace this agency's staff closes. */
export const isAgencyTtBillColumn = (backendRole: string): boolean => backendRole === TT_BILL_ROLE;

/** An hours figure without trailing zeros; the unit comes from the caller's translation. */
const hoursNumber = (value: number): string => (value % 1 === 0 ? value.toFixed(0) : String(value));

/** Agency cell formatters: rates through the surface's own wage-hiding rule, hours through its translated unit. */
export type AgencyCellFormat = { wage: (wage: number) => string; hours: (hours: string) => string };

/** A cell as the grid shows it: blank for an unset rate, hours in the T&T columns, the rate otherwise. */
export const agencyCellText = (column: AgencyDisplayColumn, wage: number | null, format: AgencyCellFormat): string => {
	if (wage === null) return "";
	return isAgencyHoursColumn(column.backend) ? format.hours(hoursNumber(wage)) : format.wage(wage);
};
